import { formatDateString } from "@circle-react/helpers/dateTimeHelpers/getLocalizedDate";
import { useMemo } from "react";

import I18n from "@/i18n-js/instance";
import { useBannerVisibility } from "@/react/components/Layout/TrialBanner/useBannerVisibility";
import { useCurrentPlanApi } from "@/react/components/SettingsApp/Plan/hooks/useCurrentPlanApi";
import { distanceBetweenDates } from "@/react/helpers/dateTimeHelpers";
import { useCountdownTimer } from "@/react/hooks/useCountdownTimer";

export const usePlanTrialBanner = () => {
  const { shouldHideBanner } = useBannerVisibility();
  const { currentPlan } = useCurrentPlanApi();

  const planTrial = currentPlan?.plan_trial || null;
  const isPlanTrialActive = planTrial?.active || false;

  const planTrialExpiringInTimer = useCountdownTimer({
    endDate: planTrial?.expires_at || undefined,
  });

  const planTrialDaysLeft = useMemo(() => {
    if (!isPlanTrialActive || !planTrial?.expires_at) return 0;
    return distanceBetweenDates(new Date(), new Date(planTrial.expires_at));
  }, [isPlanTrialActive, planTrial]);

  const planTrialEndDateFormatted = useMemo(() => {
    if (!planTrial?.expires_at) return "";
    return formatDateString(planTrial.expires_at, {
      month: "long",
      day: "2-digit",
    });
  }, [planTrial]);

  const planTrialDaysRemainingInWords = useMemo(
    () =>
      planTrialDaysLeft <= 1
        ? `1 ${I18n.t("trial_banner.day")}`
        : `${planTrialDaysLeft} ${I18n.t("trial_banner.days")}`,
    [planTrialDaysLeft],
  );

  const shouldShowPlanTrialBanner = useMemo(
    () => Boolean(!shouldHideBanner && isPlanTrialActive),
    [shouldHideBanner, isPlanTrialActive],
  );

  return {
    isPlanTrialActive,
    planTrial,
    planTrialDaysLeft,
    planTrialDaysRemainingInWords,
    planTrialExpiringInTimer,
    planTrialEndDateFormatted,
    shouldShowPlanTrialBanner,
  };
};
